/*
 * Figma `113:154` — SETUP-04 · Payment Account · Registry + Verification.
 *
 * `ManagementTemplate`: readiness above, then the registry beside the editor for whichever account
 * is open. This is the money screen — the account here produces every buyer's QR and every slip is
 * checked against it — so the two things the frame insists on are the two things the old panel left
 * implicit.
 *
 * **Readiness, before any control.** Whether there is a primary account, whether it can produce a
 * QR at all, and whether slips paid into it can be matched automatically. That last one is derived
 * rather than stored (`canAutoVerify`): an account missing its bank code still takes money, it just
 * makes every slip a manual review, and an organizer should learn that before the queue fills up.
 *
 * **The consequence of changing the primary account.** It applies to new QRs only; orders that
 * already have one keep the account they were issued against, so the amount a buyer transferred
 * still reconciles against the slip they send. Stated beside the control, per `113:332`.
 *
 * The status word in the frame is "VERIFIED". It is not used here: nothing on this screen verifies
 * an identity, and a badge saying so would promise a check JamTick does not perform. What the data
 * supports is whether *slips* can be matched automatically, and that is what the badge says.
 */
import {
  Alert, Button, Card, ConfirmDialog, ManagementTemplate, ReadinessCard, ReadinessRow,
  Select, StatusBadge, TextField, Toggle,
} from '..';
import type { ReadinessTone } from '..';
import { useState } from 'react';
import type { Destination } from '../destinations';
import { ImageSlot } from '../../components/ImageSlot';
import { PAYMENT_PROVIDERS } from '../../api';
import type { PaymentAccountDto, PaymentProvider } from '../../api';
import type { PaymentAccountsState } from './usePaymentAccounts';
import {
  PROMPTPAY_TYPE_LABEL, PROVIDER_LABEL, REF1_PROVIDERS, canAutoVerify, usePaymentAccounts,
} from './usePaymentAccounts';

function accountTitle(a: PaymentAccountDto): string {
  return `${PROVIDER_LABEL[a.provider]} · ${a.identifier}`;
}

function readiness(accounts: readonly PaymentAccountDto[]): {
  title: string; status: string; tone: ReadinessTone; detail?: string;
}[] {
  const primary = accounts.find((a) => a.isDefault && a.enabled) ?? null;
  const verifiable = primary ? canAutoVerify(primary) : false;
  return [
    {
      title: 'บัญชีหลัก',
      status: primary ? 'พร้อมรับเงิน' : accounts.length ? 'ยังไม่ได้ตั้งบัญชีหลัก' : 'ยังไม่มีบัญชี',
      tone: primary ? 'ready' : 'down',
      detail: primary ? primary.displayName : 'ผู้ซื้อจะชำระเงินไม่ได้จนกว่าจะมีบัญชีหลักที่เปิดใช้งาน',
    },
    {
      title: 'สร้าง QR',
      status: !primary ? 'ยังไม่พร้อม'
        : primary.provider === 'bank' ? (primary.qrImageKey ? 'ใช้ QR ที่อัปโหลดไว้' : 'ไม่มี QR')
          : 'สร้างอัตโนมัติได้',
      tone: !primary ? 'down'
        : primary.provider === 'bank' ? (primary.qrImageKey ? 'ready' : 'attention')
          : 'ready',
      detail: primary?.provider === 'bank' && !primary.qrImageKey
        ? 'บัญชีธนาคารไม่สร้าง QR เอง — อัปโหลด QR ของบัญชีนี้ไว้'
        : undefined,
    },
    {
      title: 'ตรวจสลิปอัตโนมัติ',
      status: verifiable ? 'ตรวจได้' : 'ต้องตรวจเอง',
      tone: verifiable ? 'ready' : 'attention',
      detail: verifiable
        ? undefined
        : REF1_PROVIDERS.includes(primary?.provider ?? 'promptpay')
          ? 'ต้องมีทั้งรหัสธนาคารและ BILLERID จึงจะจับคู่สลิปอัตโนมัติได้'
          : 'ยังไม่ได้ระบุรหัสธนาคาร — สลิปทุกใบจะเข้าคิวตรวจด้วยคน',
    },
  ];
}

export function PaymentAccountsScreen({ destination, state, permitted, role, token, eventSlug }: {
  destination: Destination;
  state: PaymentAccountsState;
  permitted: boolean;
  role?: string | null;
  token: string;
  eventSlug?: string;
}) {
  const {
    accounts, loading, loadError, error, busyId, selectedId, select,
    draft, setDraft, draftError, saving, qr, testQr, create, update, remove, makeDefault,
  } = state;
  const [confirmDelete, setConfirmDelete] = useState<PaymentAccountDto | null>(null);

  const selected = selectedId && selectedId !== 'new' ? accounts.find((a) => a.id === selectedId) ?? null : null;
  const adding = selectedId === 'new';
  const isRef1 = REF1_PROVIDERS.includes(draft.provider);

  const identifierLabel = draft.provider === 'bank' ? 'เลขที่บัญชี'
    : draft.provider === 'promptpay' ? PROMPTPAY_TYPE_LABEL[draft.promptpayType ?? 'msisdn']
      : `เลขร้านค้า ${PROVIDER_LABEL[draft.provider]}`;

  return (
    <ManagementTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      summary={
        <>
          <ReadinessRow label="ความพร้อมของการรับเงิน">
            {readiness(accounts).map((card) => <ReadinessCard key={card.title} {...card} />)}
          </ReadinessRow>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
        </>
      }
      items={
        <div className="jt-registry-split">
          <Card
            title={`บัญชีรับเงิน · ${accounts.length}`}
            action={<Button size="sm" onClick={() => select('new')}>＋ เพิ่มบัญชี</Button>}
          >
            {accounts.length === 0 && (
              <p className="jt-field-hint">ยังไม่มีบัญชี — ผู้ซื้อจะชำระเงินไม่ได้จนกว่าจะเพิ่มอย่างน้อยหนึ่งบัญชี</p>
            )}
            <ul className="jt-account-list">
              {accounts.map((account) => {
                const auto = canAutoVerify(account);
                return (
                  <li key={account.id}>
                    <button
                      type="button"
                      className="jt-account"
                      data-selected={selectedId === account.id || undefined}
                      aria-current={selectedId === account.id ? 'true' : undefined}
                      onClick={() => select(account.id)}
                    >
                      <span className="jt-account-title">{accountTitle(account)}</span>
                      <span className="jt-account-sub">
                        {[account.bankName, account.merchantName, account.displayName].filter(Boolean).join(' · ')}
                      </span>
                      <span className="jt-account-tags">
                        <StatusBadge tone={account.isDefault ? 'accent' : 'neutral'}>
                          {account.isDefault ? 'บัญชีหลัก' : 'สำรอง'}
                        </StatusBadge>
                        <StatusBadge tone={account.enabled ? 'success' : 'neutral'}>
                          {account.enabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}
                        </StatusBadge>
                        <StatusBadge tone={auto ? 'success' : 'warning'}>
                          {auto ? 'ตรวจสลิปอัตโนมัติได้' : 'ต้องตรวจสลิปเอง'}
                        </StatusBadge>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {selectedId === null ? (
            <Card title="เลือกบัญชีเพื่อแก้ไข" sub="หรือกด “＋ เพิ่มบัญชี” เพื่อสร้างใหม่">
              <p className="jt-field-hint">
                บัญชีหลักคือบัญชีที่ใช้สร้าง QR ให้ผู้ซื้อทุกคน และเป็นบัญชีที่สลิปทุกใบถูกตรวจเทียบด้วย
              </p>
            </Card>
          ) : (
            <Card
              title={adding ? 'เพิ่มบัญชีรับเงิน' : `แก้ไข ${selected ? accountTitle(selected) : ''}`}
              action={<Button size="sm" variant="ghost" onClick={() => select(null)}>ปิด</Button>}
            >
              {draftError && <Alert tone="danger" title="บันทึกไม่สำเร็จ">{draftError}</Alert>}

              <div className="jt-field-grid">
                <Select
                  label="ผู้ให้บริการ"
                  value={draft.provider}
                  options={PAYMENT_PROVIDERS.map((p) => ({ value: p, label: PROVIDER_LABEL[p] }))}
                  onChange={(e) => {
                    const provider = e.target.value as PaymentProvider;
                    setDraft({
                      ...draft,
                      provider,
                      promptpayType: provider === 'promptpay' ? (draft.promptpayType ?? 'msisdn') : null,
                    });
                  }}
                />
                <TextField
                  label="ชื่อเรียกบัญชี"
                  requiredMark
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  hint="ใช้ในหน้าจอทีมงานเท่านั้น ผู้ซื้อไม่เห็น"
                />
              </div>

              {draft.provider === 'promptpay' && (
                <Select
                  label="ประเภทพร้อมเพย์"
                  value={draft.promptpayType ?? 'msisdn'}
                  options={(['msisdn', 'natid', 'ewallet'] as const).map((t) => ({ value: t, label: PROMPTPAY_TYPE_LABEL[t] }))}
                  onChange={(e) => setDraft({ ...draft, promptpayType: e.target.value as 'msisdn' | 'natid' | 'ewallet' })}
                />
              )}

              <TextField
                label={identifierLabel}
                requiredMark
                value={draft.identifier}
                onChange={(e) => setDraft({ ...draft, identifier: e.target.value })}
                error={draft.provider === 'promptpay' ? state.identifierError : undefined}
                hint={isRef1
                  ? 'ระบบจะตัดอักขระอื่นและเปลี่ยนเป็นตัวพิมพ์ใหญ่ให้ — พิมพ์เล็กหรือขีดคั่นเป็นสาเหตุที่ K-Plus ปฏิเสธการจ่าย'
                  : draft.provider === 'promptpay' ? 'กรอกเฉพาะตัวเลขตามประเภทพร้อมเพย์ที่เลือก'
                    : undefined}
              />

              <div className="jt-field-grid">
                <TextField
                  label="ชื่อธนาคาร"
                  value={draft.bankName ?? ''}
                  onChange={(e) => setDraft({ ...draft, bankName: e.target.value || null })}
                />
                <TextField
                  label="รหัสธนาคาร (BOT 3 หลัก)"
                  value={draft.bankCode ?? ''}
                  onChange={(e) => setDraft({ ...draft, bankCode: e.target.value || null })}
                  hint="ไม่มีรหัสนี้ ระบบจะจับคู่ผู้รับบนสลิปอัตโนมัติไม่ได้"
                />
              </div>

              {isRef1 && (
                <TextField
                  label="BILLERID"
                  value={draft.receiverProxyId ?? ''}
                  onChange={(e) => setDraft({ ...draft, receiverProxyId: e.target.value || null })}
                  hint="ไม่ได้อยู่ใน QR จึงต้องกรอกเอง — ใช้ตรวจว่าสลิปจ่ายเข้าร้านนี้จริง"
                />
              )}

              {draft.provider !== 'bank' ? (
                <Card title="ทดสอบ QR" sub="สร้าง QR จากค่าที่กรอกไว้ โดยยังไม่บันทึก">
                  <div className="jt-inline-actions">
                    <Button size="sm" variant="secondary" disabled={qr.loading || !draft.identifier || !!state.identifierError} onClick={() => void testQr()}>
                      {qr.loading ? 'กำลังสร้าง…' : 'สร้าง QR ทดสอบ'}
                    </Button>
                  </div>
                  {qr.error && <Alert tone="danger" title="สร้าง QR ไม่สำเร็จ">{qr.error}</Alert>}
                  {qr.dataUrl && <img className="jt-qr-test" src={qr.dataUrl} alt="QR ทดสอบของบัญชีนี้" width={200} height={200} />}
                </Card>
              ) : (
                <ImageSlot
                  token={token}
                  eventSlug={eventSlug}
                  imageKey={draft.qrImageKey ?? null}
                  onChange={(key) => setDraft({ ...draft, qrImageKey: key })}
                  alt="QR ของบัญชีธนาคาร"
                  aspect="1 / 1"
                  size={200}
                  fit="contain"
                  label="QR ของบัญชีนี้"
                  sub="บัญชีธนาคารสร้าง QR เองไม่ได้ — อัปโหลด QR ที่ได้จากแอปธนาคาร"
                  placeholder="ลากรูป QR มาวาง"
                />
              )}

              {!adding && selected && (
                <>
                  <Alert tone="info" title="เปลี่ยนบัญชีหลักมีผลกับ QR ใหม่เท่านั้น">
                    ออร์เดอร์ที่สร้าง QR ไปแล้วจะยังใช้ข้อมูลบัญชีเดิม เพื่อให้ยอดอ้างอิงตรงกับสลิปที่ผู้ซื้อส่งมา
                  </Alert>
                  <Toggle
                    label="เปิดใช้งานบัญชีนี้"
                    checked={selected.enabled}
                    disabled={busyId === selected.id}
                    onChange={(e) => void update(selected.id, { enabled: e.target.checked })}
                    consequence={selected.isDefault
                      ? 'นี่คือบัญชีหลัก — ปิดแล้วผู้ซื้อจะชำระเงินไม่ได้จนกว่าจะตั้งบัญชีอื่นแทน'
                      : 'บัญชีสำรองที่ปิดไว้จะไม่ถูกใช้เมื่อบัญชีหลักมีปัญหา'}
                  />
                </>
              )}

              <div className="jt-inline-actions">
                {adding ? (
                  <Button variant="primary" disabled={saving || !!state.identifierError} onClick={() => void create()}>
                    {saving ? 'กำลังบันทึก…' : 'บันทึกบัญชี'}
                  </Button>
                ) : selected && (
                  <>
                    <Button
                      variant="primary"
                      disabled={saving || !!state.identifierError}
                      onClick={() => void update(selected.id, { ...draft, identifier: draft.identifier })}
                    >
                      {saving ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
                    </Button>
                    {!selected.isDefault && (
                      <Button
                        variant="secondary"
                        disabled={busyId === selected.id || !selected.enabled}
                        onClick={() => void makeDefault(selected.id)}
                      >
                        ตั้งเป็นบัญชีหลัก
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      disabled={busyId === selected.id || selected.isDefault}
                      onClick={() => setConfirmDelete(selected)}
                    >
                      ลบบัญชี
                    </Button>
                  </>
                )}
              </div>
              {selected?.isDefault && (
                <p className="jt-field-hint">ลบบัญชีหลักไม่ได้ — ตั้งบัญชีอื่นเป็นบัญชีหลักก่อน</p>
              )}
            </Card>
          )}
        </div>
      }
      detail={
        <ConfirmDialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => {
            const target = confirmDelete;
            setConfirmDelete(null);
            if (target) void remove(target.id);
          }}
          title={`ลบบัญชี ${confirmDelete ? accountTitle(confirmDelete) : ''}`}
          confirmLabel="ลบบัญชีนี้"
          busy={!!confirmDelete && busyId === confirmDelete.id}
          consequences={[
            'บัญชีนี้จะไม่ถูกใช้สร้าง QR ให้ออร์เดอร์ใหม่อีก',
            // TICK-151 — an unpaid order is re-resolved, never left on a deleted receiver, and there
            // is no platform account to fall back to when this was the last enabled one.
            confirmDelete && isLastEnabled(accounts, confirmDelete)
              ? 'นี่คือบัญชีรับเงินสุดท้ายที่เปิดใช้งาน — หลังลบ ผู้ซื้อจะไม่เห็น QR หรือเลขบัญชีและชำระเงินไม่ได้ จนกว่าจะเพิ่มบัญชีใหม่ (JamTick ไม่รับเงินแทนผู้จัดงาน)'
              : 'ออร์เดอร์ที่ยังไม่ชำระจะได้ QR จากบัญชีอื่นที่เปิดใช้งานแทน',
            'การลบย้อนกลับไม่ได้ — ถ้าต้องใช้อีกจะต้องเพิ่มบัญชีใหม่และกรอกข้อมูลใหม่ทั้งหมด',
          ]}
        />
      }
    />
  );
}

/** TICK-151 — whether removing `target` leaves the Event with no enabled receiving account. */
export function isLastEnabled(accounts: PaymentAccountDto[], target: PaymentAccountDto): boolean {
  return target.enabled && !accounts.some((a) => a.id !== target.id && a.enabled);
}

export function PaymentAccountsDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = usePaymentAccounts(token, eventSlug);
  return (
    <PaymentAccountsScreen
      destination={destination} state={state} permitted={permitted} role={role}
      token={token} eventSlug={eventSlug}
    />
  );
}
